/**
 * ProductCustomerTypesPage — Products & Features · Product Customer Types.
 *
 * Flat editable mapping table — one row per (productCode, customerType) — that
 * declares which customer types may open each product. Built to match the
 * ProductsPage CRUD reference: reload via config read, a DataTable with trailing
 * Edit / Delete ghost buttons, a top "New mapping" Card action, a ModalShell
 * form using FormField primitives (BOTH fields are dropdowns — no free text),
 * a save() handler, and a window.confirm soft-delete.
 *
 * Reads:
 *   client.retail.config.productCustomerTypes()  // [{ productCode, customerType }, …]
 *   client.retail.config.products()              // FK source for the productCode dropdown
 *
 * Writes go through the typed bearer-only admin surface (first arg '' = no
 * bypass key; bearer-only, no bypass prompt):
 *   client.retail.admin.products.productCustomerTypes.upsert('', { productCode, customerType })
 *   client.retail.admin.products.productCustomerTypes.remove('', productCode, customerType)
 *
 * NOTE on naming: the task brief used `productKey`, but the live wire shape and
 * the typed `ProductCustomerTypeUpsertBody` use `productCode` (config read rows
 * also key on `productCode`). This file follows the actual typed surface so it
 * stays TS-strict-clean.
 *
 * The customerType option list is copied VERBATIM from the legacy editor
 * apps/ROP-teams/src/modules/Admin/editors/productCustomerTypesEditor.tsx
 * (its KNOWN_CUSTOMER_TYPES) — no invented customer-type values.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Product } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { SelectField, TextField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

// Wire row as returned by GET /v3/config/product-customer-types. The config
// read is typed as Record<string, unknown>; the allow-list rows carry at least
// these two columns. We cast the read result into this local interface.
interface ProductCustomerTypeRow {
  productCode: string;
  customerType: string;
}

// All known v3 customer types — copied VERBATIM (value + label) from the legacy
// editor's KNOWN_CUSTOMER_TYPES (productCustomerTypesEditor.tsx). Mirrors the
// EntityRules.CustomerType domain. Do NOT invent values here.
const CUSTOMER_TYPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'Individual', label: 'Individual' },
  { value: 'JointTenants', label: 'Joint' },
  { value: 'SoleProprietor', label: 'Sole Prop' },
  { value: 'LLC', label: 'LLC' },
  { value: 'Corporation', label: 'Corp' },
  { value: 'Partnership', label: 'Partnership' },
  { value: 'NonProfit', label: 'NonProfit' },
  { value: 'RevocableTrust', label: 'Rev Trust' },
  { value: 'IrrevocableTrust', label: 'Irrev Trust' },
  { value: 'Estate', label: 'Estate' },
  { value: 'Fiduciary', label: 'Fiduciary' },
  { value: 'Government', label: 'Govt' },
];

interface EditDraft {
  isNew: boolean;
  productCode: string;
  customerType: string;
}

function rowToDraft(r: ProductCustomerTypeRow): EditDraft {
  return {
    isNew: false,
    productCode: r.productCode,
    customerType: r.customerType,
  };
}

function blankDraft(): EditDraft {
  return {
    isNew: true,
    productCode: '',
    customerType: '',
  };
}

function customerTypeLabel(value: string): string {
  return CUSTOMER_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

export function ProductCustomerTypesPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<ProductCustomerTypeRow> | null>(null);
  const [products, setProducts] = useState<ReadonlyArray<Product>>([]);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const [pctResp, prodResp] = await Promise.all([
        client.retail.config.productCustomerTypes(),
        client.retail.config.products(),
      ]);
      if (pctResp.success && Array.isArray(pctResp.data)) {
        setRows(pctResp.data as unknown as ReadonlyArray<ProductCustomerTypeRow>);
      } else {
        setError(pctResp.error ?? 'Failed to load product customer types.');
        return;
      }
      if (prodResp.success && Array.isArray(prodResp.data)) {
        setProducts(prodResp.data);
      } else {
        setProducts([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [client]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // FK dropdown options for productCode — built from the live products catalog
  // (NOT free-text). Leading blank prompts a choice on a new mapping.
  const productOptions = useMemo(
    () => [
      { value: '', label: '— select a product —' },
      ...products.map((p) => ({
        value: p.code,
        label: `${p.code} — ${p.name}`,
      })),
    ],
    [products],
  );

  // customerType dropdown options, with a leading blank for a new mapping.
  const customerTypeSelectOptions = useMemo(
    () => [{ value: '', label: '— select a customer type —' }, ...CUSTOMER_TYPE_OPTIONS],
    [],
  );

  // Lookup of code → name for display in the table's Product column.
  const productNameByCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of products) m.set(p.code, p.name);
    return m;
  }, [products]);

  const filtered = useMemo(() => {
    if (!rows) return rows;
    if (!filter.trim()) return rows;
    const n = filter.toLowerCase();
    return rows.filter(
      (r) =>
        r.productCode.toLowerCase().includes(n) ||
        r.customerType.toLowerCase().includes(n) ||
        customerTypeLabel(r.customerType).toLowerCase().includes(n) ||
        (productNameByCode.get(r.productCode) ?? '').toLowerCase().includes(n),
    );
  }, [rows, filter, productNameByCode]);

  const remove = useCallback(
    async (productCode: string, customerType: string): Promise<void> => {
      if (
        !window.confirm(
          `Remove mapping ${productCode} → ${customerTypeLabel(customerType)}? ` +
            'That product/customer-type pairing will be deleted.',
        )
      ) {
        return;
      }
      setError(null);
      try {
        const resp = await client.retail.admin.products.productCustomerTypes.remove(
          '',
          productCode,
          customerType,
        );
        if (resp.success === false) {
          setError(resp.error ?? 'Delete failed.');
          return;
        }
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [client, reload],
  );

  const columns: ReadonlyArray<DataTableColumn<ProductCustomerTypeRow>> = useMemo(
    () => [
      {
        header: 'Product',
        cell: (r) => (
          <div className="cell-actions">
            <code className="cell-mono--bold">{r.productCode}</code>
            <span className="cell-dim">{productNameByCode.get(r.productCode) ?? '—'}</span>
          </div>
        ),
      },
      {
        header: 'Customer Type',
        cell: (r) => <Pill tone="muted">{customerTypeLabel(r.customerType)}</Pill>,
      },
      {
        header: '',
        cell: (r) => (
          <div className="cell-actions">
            <Button size="sm" variant="ghost" onClick={() => setDraft(rowToDraft(r))}>
              Edit
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void remove(r.productCode, r.customerType)}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [remove, productNameByCode],
  );

  const save = useCallback(
    async (d: EditDraft): Promise<void> => {
      if (!d.productCode.trim() || !d.customerType.trim()) {
        setSaveErr('Both a product and a customer type are required.');
        return;
      }
      setSaving(true);
      setSaveErr(null);
      try {
        const resp = await client.retail.admin.products.productCustomerTypes.upsert('', {
          productCode: d.productCode.trim(),
          customerType: d.customerType.trim(),
        });
        if (resp.success === false) {
          setSaveErr(resp.error ?? 'Save failed.');
          return;
        }
        setDraft(null);
        await reload();
      } catch (e) {
        setSaveErr(e instanceof Error ? e.message : String(e));
      } finally {
        setSaving(false);
      }
    },
    [client, reload],
  );

  if (error) return <Banner tone="err">Could not load product customer types: {error}</Banner>;
  if (!rows) return <Spinner label="Loading product customer types…" />;

  return (
    <>
      <Card
        title={`Product Customer Types — ${filtered?.length ?? 0} of ${rows.length}`}
        actions={
          <div className="users-filter">
            <TextField
              label=""
              value={filter}
              onChange={setFilter}
              placeholder="Filter by product code, name, or customer type…"
            />
            <Button onClick={() => setDraft(blankDraft())}>New mapping</Button>
          </div>
        }
      >
        <DataTable<ProductCustomerTypeRow>
          columns={columns}
          rows={filtered ?? []}
          rowKey={(r) => `${r.productCode}::${r.customerType}`}
          emptyState={<p className="empty">No mappings match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={
          draft
            ? draft.isNew
              ? 'New mapping'
              : `Edit mapping — ${draft.productCode} → ${customerTypeLabel(draft.customerType)}`
            : ''
        }
        onClose={() => (saving ? undefined : setDraft(null))}
        width={520}
        actions={
          draft ? (
            <>
              <Button variant="ghost" onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={() => void save(draft)} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : null
        }
      >
        {draft && (
          <>
            {saveErr && <Banner tone="err">{saveErr}</Banner>}
            <SelectField
              label="Product"
              value={draft.productCode}
              onChange={(v) => setDraft({ ...draft, productCode: v })}
              options={productOptions}
              disabled={!draft.isNew}
              required
              hint={
                draft.isNew
                  ? 'FK to the Products catalog (not free-text).'
                  : 'Part of the primary key — read-only after create.'
              }
            />
            <SelectField
              label="Customer Type"
              value={draft.customerType}
              onChange={(v) => setDraft({ ...draft, customerType: v })}
              options={customerTypeSelectOptions}
              disabled={!draft.isNew}
              required
              hint={
                draft.isNew
                  ? 'EntityRules.CustomerType domain (not free-text).'
                  : 'Part of the primary key — read-only after create.'
              }
            />
          </>
        )}
      </ModalShell>
    </>
  );
}
